import { DataSTag, Sel } from '#~/stag/index.js';
import { Button } from '@studio/design-system/button';
import { useMemo, useState } from 'react';
import { twMerge } from 'cn';
import { Error } from './components/index.js';
import { Spinner } from '@studio/design-system/spinner';
import { useAnalytics } from '#~/analytics/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';

interface Props {
  className?: string;
  ctaType?: 'add' | 'edit' | 'update';
  error?: string;
  title?: string | undefined;
  subline?: string | undefined;
  onCta?: () => Promise<void>;
  loading: boolean;
}

export const PaymentMethodCard = ({
  loading,
  className,
  ctaType = 'add',
  error,
  onCta,
  title,
  subline
}: Props) => {
  const [isLoading, setLoading] = useState(false);
  const { track } = useAnalytics();

  const handleClick = async () => {
    setLoading(true);
    try {
      // Track the payment method event
      if (ctaType === 'add') {
        track(EVENT_NAMES.ADD_PAYMENT_METHOD, EVENT_CATEGORIES.BILLING, {
          paymentMethodType: 'credit_card'
        });
      }

      await onCta?.();
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const cardUi = useMemo(() => {
    switch (ctaType) {
      case 'add': {
        return (
          <div className='flex grow items-center gap-6'>
            <i className='i-mdi-credit-card-plus-outline text-2xl' />
            <div className='flex flex-col justify-center gap-6'>
              <div className='font-demibold'>{title ?? 'Add card for cloud usage'}</div>
              <div className='text-sm font-regular text-muted'>
                {subline ?? 'Saving a card does not buy Standard.'}
              </div>
            </div>
          </div>
        );
      }
      case 'edit':
      case 'update': {
        return (
          <div className='flex grow items-center gap-6'>
            <i className='i-mdi-credit-card-outline text-2xl' />
            <div className='flex flex-col justify-center gap-6'>
              <div className='font-demibold'>{title ?? ''}</div>
              <div className='text-sm font-regular text-muted'>{subline ?? ''}</div>
            </div>
          </div>
        );
      }
    }

    return null;
  }, [ctaType, title, subline]);

  const cta = useMemo(() => {
    switch (ctaType) {
      case 'edit': {
        return (
          <Button
            {...DataSTag(Sel.AccountSettings, 'cloud-payment-method')}
            disabled={isLoading}
            onClick={handleClick}
            variant='secondary'
          >
            {isLoading ? <Spinner className='mx-[32px]' /> : 'Edit Payment Method'}
          </Button>
        );
      }
      case 'add': {
        return (
          <Button
            {...DataSTag(Sel.AccountSettings, 'cloud-payment-method')}
            disabled={isLoading}
            onClick={handleClick}
            variant='primary'
          >
            {isLoading ? <Spinner className='mx-[32px]' /> : 'Add Payment method'}
          </Button>
        );
      }
      case 'update': {
        return (
          <Button
            {...DataSTag(Sel.AccountSettings, 'cloud-payment-method')}
            disabled={isLoading}
            onClick={handleClick}
            variant='primary'
          >
            {isLoading ? <Spinner className='mx-[32px]' /> : 'Update Payment Method'}
          </Button>
        );
      }
    }
  }, [ctaType, onCta, isLoading]);

  if (loading) {
    return (
      <div className={twMerge('flex flex-col rounded-8 bg-dark', className)}>
        <div className='flex items-center justify-between border-b-1 border-b-base-100 p-20'>
          <div className='mb-0 text-lg font-demibold leading-100'>Cloud payment method</div>
        </div>

        <div className='flex grow flex-col p-20'>
          <div className='flex grow items-center gap-6 text-center'>
            <Spinner className='mx-auto' />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={twMerge('flex flex-col rounded-8 bg-dark', className)}>
      <div className='flex items-center justify-between border-b-1 border-b-base-100 p-20'>
        <div className='mb-0 text-lg font-demibold leading-100'>Cloud payment method</div>
        {/* <i className='i-mdi-credit-card-fast text-2xl text-primary' /> */}
      </div>
      <div className='flex grow flex-col p-20'>
        {cardUi}
        <div className='mt-24 flex items-end justify-between gap-x-12'>
          {cta}
          {!!error && <Error>{error}</Error>}
        </div>
      </div>
    </div>
  );
};
